// src/pages/TempleFormDrawer.jsx
import React from 'react';
import { Drawer, Form, Input, Button, Select, Divider, message, InputNumber, Space } from 'antd';
import ThaiAddressSelector from '../../components/ThaiAddressSelector';
import MGRSCoordinateInput from '../..//components/MGRSCoordinateInput';
import { MGRS } from "@ngageoint/mgrs-js";


const { Option } = Select;
const TempleFormDrawer = ({ open, onClose, onSubmit, initialValues }) => {
  const [form] = Form.useForm();
  const [coordinates, setCoordinates] = React.useState(null);

  // หากมีค่า initialValues ให้เติมในฟอร์ม
  React.useEffect(() => {
    //console.log('initialValues', initialValues);
    form.setFieldsValue(initialValues || {});
  }, [initialValues, form]);

  const handleFinish = (values) => {

    if (coordinates) {
      onSubmit({ ...values, latitude: coordinates.latitude, longitude: coordinates.longitude });
    } else {
      onSubmit({ ...values });
    }

    form.resetFields();
  };

  const ConvertLatLngToMGRS = async () => {
    const values = form.getFieldsValue();
    const lat = parseFloat(values.latitude);
    const lng = parseFloat(values.longitude);
    //console.log('getFieldValue', lat, lng);

    if (isNaN(lat) || isNaN(lng)) {
      return alert('กรุณากรอกพิกัดให้ถูกต้อง');
    }

    const mgrs = latLngToMGRS(lat, lng);

    form.setFieldsValue({
      mgrs: mgrs.zone + ' ' + mgrs.square + ' ' + mgrs.easting + ' ' + mgrs.northing,
    });

  };

  function latitudeToZoneLetter(lat) {
    if (lat >= 84) return 'X';
    if (lat >= 72) return 'W';
    if (lat >= 64) return 'V';
    if (lat >= 56) return 'U';
    if (lat >= 48) return 'T';
    if (lat >= 40) return 'S';
    if (lat >= 32) return 'R';
    if (lat >= 24) return 'Q';
    if (lat >= 16) return 'P';
    if (lat >= 8) return 'N';
    if (lat >= 0) return 'M';
    if (lat >= -8) return 'L';
    if (lat >= -16) return 'K';
    if (lat >= -24) return 'J';
    if (lat >= -32) return 'H';
    if (lat >= -40) return 'G';
    if (lat >= -48) return 'F';
    if (lat >= -56) return 'E';
    if (lat >= -64) return 'D';
    if (lat >= -72) return 'C';
    return 'Z'; // Invalid
  }

  const e100kLetters = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const n100kLetters = [
    "ABCDEFGH", "JKLMNPQR", "STUVWXYZ", "ABCDEFGH", "JKLMNPQR", "STUVWXYZ"
  ];

  function get100kID(easting, northing, zoneNumber) {
    const setParm = get100kSetForZone(zoneNumber);
    const columnLetters = "ABCDEFGHJKLMNPQRSTUVWXYZ"; // ไม่มี I, O
    const rowLetters = "ABCDEFGHJKLMNPQRSTUV";        // 20 ตัว ไม่มี I, O

    const e100k = Math.floor(easting / 100000);
    const n100k = Math.floor(northing / 100000) % 20; // 20 แถววนซ้ำ

    const colLetter = columnLetters[(e100k - 1 + (setParm - 1) * 8) % 24];
    const rowLetter = rowLetters[(n100k + getRowOffset(setParm)) % 20];

    return colLetter + rowLetter;
  }

  function get100kSetForZone(zoneNumber) {
    const setNumber = (zoneNumber % 6 === 0) ? 6 : zoneNumber % 6;
    return setNumber;
  }

  function getRowOffset(setNumber) {
    const rowOffsets = [0, 5, 0, 5, 0, 5];
    return rowOffsets[setNumber - 1];
  }

  function zeroPad(num, places) {
    return String(num).padStart(places, '0');
  }


  function latLngToMGRS(lat, lng, accuracy = 5) {

    // --- UTM Conversion ---
    const a = 6378137;
    const f = 1 / 298.257223563;
    const k0 = 0.9996;
    const e = Math.sqrt(f * (2 - f));

    const zoneNumber = Math.floor((lng + 180) / 6) + 1;
    const zoneLetter = latitudeToZoneLetter(lat);
    const λ0 = ((zoneNumber - 1) * 6 - 180 + 3) * Math.PI / 180;

    const φ = lat * Math.PI / 180;
    const λ = lng * Math.PI / 180;

    const N = a / Math.sqrt(1 - Math.pow(e * Math.sin(φ), 2));
    const T = Math.pow(Math.tan(φ), 2);
    const C = Math.pow(e * Math.cos(φ), 2) / (1 - Math.pow(e, 2));
    const A = Math.cos(φ) * (λ - λ0);

    const M = a * ((1 - Math.pow(e, 2) / 4 - 3 * Math.pow(e, 4) / 64 - 5 * Math.pow(e, 6) / 256) * φ
      - (3 * Math.pow(e, 2) / 8 + 3 * Math.pow(e, 4) / 32 + 45 * Math.pow(e, 6) / 1024) * Math.sin(2 * φ)
      + (15 * Math.pow(e, 4) / 256 + 45 * Math.pow(e, 6) / 1024) * Math.sin(4 * φ)
      - (35 * Math.pow(e, 6) / 3072) * Math.sin(6 * φ));

    let easting = k0 * N * (A + (1 - T + C) * Math.pow(A, 3) / 6
      + (5 - 18 * T + T * T + 72 * C - 58 * Math.pow(e, 2)) * Math.pow(A, 5) / 120) + 500000;

    let northing = k0 * (M + N * Math.tan(φ) * (Math.pow(A, 2) / 2
      + (5 - T + 9 * C + 4 * C * C) * Math.pow(A, 4) / 24
      + (61 - 58 * T + T * T + 600 * C - 330 * Math.pow(e, 2)) * Math.pow(A, 6) / 720));

    if (lat < 0) northing += 10000000;

    const id100k = get100kID(easting, northing, zoneNumber);
    const zone = `${zoneNumber}${zoneLetter}`;

    const eShort = Math.floor(easting % 100000);
    const nShort = Math.floor(northing % 100000);

    const digits = Math.pow(10, 5 - accuracy);
    const eRounded = Math.floor(eShort / digits);
    const nRounded = Math.floor(nShort / digits);

    const eStr = zeroPad(eRounded, accuracy);
    const nStr = zeroPad(nRounded, accuracy);

    return {
      mgrs: `${zone}${id100k}${eStr}${nStr}`,
      zone: zone,
      square: id100k,
      easting: eStr,
      northing: nStr,
    };
  }

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      // ตัวอย่าง input: "16.44194377571047, 101.1624006812391"
      const parts = text.split(',').map(p => p.trim());

      if (parts.length !== 2) {
        message.error('รูปแบบพิกัดไม่ถูกต้อง (ควรเป็น "lat, lng")');
        return;
      }

      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);

      if (isNaN(lat) || isNaN(lng)) {
        message.error('ค่าพิกัดไม่ใช่ตัวเลขที่ถูกต้อง');
        return;
      }

      // Set ค่าใน Form
      form.setFieldsValue({
        latitude: lat,
        longitude: lng,
      });


      //แปลงพิกัดจาก latlong ---> MGRS
      ConvertLatLngToMGRS();

      message.success('วางพิกัดจากคลิปบอร์ดเรียบร้อยแล้ว');
    } catch (err) {
      message.error('ไม่สามารถอ่านจาก Clipboard ได้');
    }
  };

  return (
    <Drawer
      title={initialValues ? 'แก้ไขข้อมูลวัด' : 'เพิ่มข้อมูลวัด'}
      width={600}
      onClose={() => {
        form.resetFields();
        onClose();
      }}
      open={open}
      styles={{
        body: {
          paddingBottom: 80,
        },
      }}
      extra={
        <Space>
          <Button onClick={onClose}>ยกเลิก</Button>
          <Button type="primary" onClick={() => form.submit()}>
            บันทึก
          </Button>
        </Space>
      }
      destroyOnHidden={true}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={handleFinish}
        initialValues={{
          latitude: 0,
          longitude: 0
        }}
      >
        <Divider orientation="left">ข้อมูลพื้นฐานวัด</Divider>
        <Form.Item name="name" label="ชื่อวัด" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="moo" label="หมู่ที่">
          <Input />
        </Form.Item>
        <Form.Item name="address" label="หมู่บ้าน">
          <Input />
        </Form.Item>

        {initialValues ? (
          <>
            <Form.Item name="addr" value={{province:initialValues.province, amphur: initialValues.district, district: initialValues.subdistrict}}>
              <ThaiAddressSelector value={{province:initialValues.province, amphur: initialValues.district, district: initialValues.subdistrict}}  province={initialValues.province} amphur={initialValues.district} subdistrict={initialValues.subdistrict}/>
            </Form.Item>
          </>
        ) : (
          <>
            <Form.Item name="addr">
              <ThaiAddressSelector />
            </Form.Item>
          </>
        )}

        <Form.Item name="postal_code" label="รหัสไปรษณีย์">
          <Input />
        </Form.Item>
        <MGRSCoordinateInput form={form} onEnter={(value) => {
          setCoordinates(value);
          form.setFieldValue({ latitude: value.latitude, longitude: value.longitude });
        }} />
        <Button type="primary" onClick={handlePasteFromClipboard}>
          วางพิกัดจากคลิปบอร์ด
        </Button>
        <Form.Item name="latitude" label="latitude" onKeyPress={(value) => {
          console.log('converting...', value)
          ConvertLatLngToMGRS();
        }}>
          <Input />
        </Form.Item>
        <Form.Item name="longitude" label="longitude">
          <Input />
        </Form.Item>
        <Form.Item name="phone_number" label="เบอร์โทร">
          <Input />
        </Form.Item>
        <Form.Item name="website" label="เว็บไซต์">
          <Input />
        </Form.Item>
        <Form.Item name="established_year" label="ปีที่ก่อตั้ง">
          <Input />
        </Form.Item>
        <Form.Item name="type" label="ประเภท">
          <Select>
            <Option value="พัทธสีมา">พัทธสีมา</Option>
            <Option value="วิสุงคามสีมา">วิสุงคามสีมา</Option>
            <Option value="พระอารามหลวง">พระอารามหลวง</Option>
            <Option value="วัดราษฎร์">วัดราษฎร์</Option>
            <Option value="ที่พักสงฆ์">ที่พักสงฆ์</Option>
          </Select>
        </Form.Item>
        <Form.Item name="sect" label="สังกัดนิกาย">
          <Select>
            <Option value="ธรรมยุต">ธรรมยุต</Option>
            <Option value="มหานิกาย">มหานิกาย</Option>
          </Select>
        </Form.Item>
        <Divider orientation="left">ข้อมูลเจ้าอาวาส</Divider>
        <Form.Item name="abbot_name" label="เจ้าอาวาส">
          <Input />
        </Form.Item>
        <Form.Item name="abbot_old_name" label="นามเดิม">
          <Input />
        </Form.Item>
        <Form.Item name="abbot_lastname" label="นามสกุล">
          <Input />
        </Form.Item>
        <Form.Item name="abbot_dharma_name" label="ฉายา">
          <Input />
        </Form.Item>
        <Form.Item name="abbot_position" label="ตำแหน่งทางการปกครอง">
          <Input />
        </Form.Item>
        <Form.Item name="abbot_age" label="อายุ">
          <InputNumber />
        </Form.Item>
        <Form.Item name="abbot_pansa" label="พรรษา">
          <InputNumber />
        </Form.Item>
        <Form.Item name="abbot_education" label="วิทยฐานะทางโลก">
          <Input />
        </Form.Item>
        <Form.Item name="abbot_buddhist_education" label="วิทยฐานะทางธรรม">
          <Input />
        </Form.Item>
        <Form.Item name="description" label="รายละเอียดเพิ่มเติมอื่นๆ">
          <Input.TextArea rows={4} />
        </Form.Item>
        {/* <Form.Item>
          <Button type="primary" htmlType="submit">
            บันทึก
          </Button>
        </Form.Item> */}
      </Form>
    </Drawer>
  );
};

export default TempleFormDrawer;
